/** AssistantPanel — EEngDeS Copilot chat sidebar (Claude Sonnet 4.5). */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Robot, PaperPlaneTilt } from "@phosphor-icons/react";
import { formatApiErrorDetail } from "@/lib/api";

export default function AssistantPanel({ projectId }) {
  const [msgs, setMsgs] = useState([
    { role: "assistant", content: "I'm EEngDeS Copilot. Ask me about breaker sizing, conductor ampacity, transformer KVA, NEC / CEC / IEEE code references, or how to balance loads on your panel." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef();

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" });
  }, [msgs]);

  const send = async () => {
    if (!input.trim() || busy) return;
    const message = input.trim();
    setMsgs((m) => [...m, { role: "user", content: message }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/assist", { project_id: projectId, message });
      setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", content: "I couldn't reach the AI service. " + formatApiErrorDetail(e.response?.data?.detail || "") }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <aside className="border-l border-white/10 bg-[#050608] flex flex-col h-[calc(100vh-64px-65px-49px)] lg:sticky lg:top-[178px]" data-testid="ai-panel">
      <div className="p-4 border-b border-[#00E5FF]/20 flex items-center gap-2">
        <Robot weight="duotone" size={20} className="text-[#00E5FF]" />
        <div>
          <div className="font-display font-bold">EEngDeS Copilot</div>
          <div className="label-tag text-[10px]">Claude Sonnet 4.5</div>
        </div>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-messages">
        {msgs.map((m, i) => (
          <div key={`${m.role}-${i}-${(m.content || "").slice(0, 12)}`} className={`text-sm leading-relaxed ${m.role === "user" ? "text-white" : "text-gray-300"}`}>
            <div className="label-tag mb-1">{m.role === "user" ? "› YOU" : "› COPILOT"}</div>
            <div className="font-mono whitespace-pre-wrap">{m.content}</div>
          </div>
        ))}
        {busy && (
          <div className="text-gray-500 font-mono text-sm">
            <div className="label-tag mb-1">› COPILOT</div>
            <div className="shimmer-line h-3 w-32 bg-white/5" />
          </div>
        )}
      </div>
      <div className="border-t border-white/10 p-3 pb-16 lg:pb-3 lg:pr-16">
        <div className="flex items-end gap-2">
          <textarea value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Size a feeder, validate against code…"
            rows={2} className="flex-1 bg-black/60 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none resize-none"
            data-testid="ai-input" />
          <button onClick={send} disabled={busy || !input.trim()} className="btn-primary p-2.5" data-testid="ai-send">
            <PaperPlaneTilt weight="fill" size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}
